import { useMemo, useState } from "react"
import { AdminLayout } from "@/components/Admin/AdminLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { router } from "@inertiajs/react"
import {
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
} from "recharts"
import { RefreshCw, BarChart3, PieChart, TrendingUp, Activity, Filter } from "lucide-react"

interface TestDataItem {
  escola: string | null
  serie: string | null
  sala: string | null
  tipo_eneagrama: number | null
  pontuacoes_avancadas: Record<string, number> | null
  microperfil: string | null
  teste_avancado_completo: boolean
}

interface CompletionStats {
  basic: { total: number; complete: number; rate: number }
  advanced: { total: number; complete: number; rate: number }
}

interface TimeSeriesItem {
  date: string
  basic: number
  advanced: number
}

interface Props {
  completion_stats: CompletionStats
  time_series: TimeSeriesItem[]
  test_data: TestDataItem[]
}

const ENEAGRAMA_COLORS = [
  "#7c3aed", "#2563eb", "#f97316", "#10b981", "#ec4899",
  "#0ea5e9", "#eab308", "#ef4444", "#22c55e",
]

const HEBAG_COLORS: Record<string, string> = {
  H: "#3b82f6", E: "#8b5cf6", B: "#22c55e", A: "#f59e0b", G: "#64748b",
}

const HEBAG_NAMES: Record<string, string> = {
  H: "Humanas", E: "Exatas", B: "Biológicas", A: "Artes", G: "Gestão",
}

const HEBAG_KEYS = ["H", "E", "B", "A", "G"] as const

const ENEAGRAMA_NAMES: Record<number, string> = {
  1: "O Perfeccionista", 2: "O Prestativo", 3: "O Realizador",
  4: "O Individualista", 5: "O Investigador", 6: "O Leal",
  7: "O Entusiasta", 8: "O Desafiador", 9: "O Pacificador",
}

export default function Analytics({ completion_stats, time_series, test_data }: Props) {
  const [filterEscola, setFilterEscola] = useState("")
  const [filterSerie, setFilterSerie] = useState("")
  const [filterSala, setFilterSala] = useState("")

  const escolas = useMemo(() =>
    Array.from(new Set(test_data.map((r) => r.escola).filter(Boolean) as string[])).sort(),
    [test_data]
  )

  const series = useMemo(() =>
    Array.from(new Set(test_data.map((r) => r.serie).filter(Boolean) as string[])).sort(),
    [test_data]
  )

  const salas = useMemo(() =>
    Array.from(new Set(test_data.map((r) => r.sala).filter(Boolean) as string[])).sort(),
    [test_data]
  )

  const filteredData = useMemo(() => {
    return test_data.filter((item) => {
      if (filterEscola && item.escola !== filterEscola) return false
      if (filterSerie && item.serie !== filterSerie) return false
      if (filterSala && item.sala !== filterSala) return false
      return true
    })
  }, [test_data, filterEscola, filterSerie, filterSala])

  const hasFilters = filterEscola || filterSerie || filterSala

  const filterLabel = useMemo(() => {
    const parts: string[] = []
    if (filterEscola) parts.push(filterEscola)
    if (filterSerie) parts.push(filterSerie)
    if (filterSala) parts.push(`Sala ${filterSala}`)
    return parts.length > 0 ? parts.join(" - ") : "Plataforma"
  }, [filterEscola, filterSerie, filterSala])

  // Eneagrama distribution
  const eneagramaDistribution = useMemo(() => {
    const counts: Record<number, number> = {}
    filteredData.forEach((item) => {
      if (item.tipo_eneagrama) {
        counts[item.tipo_eneagrama] = (counts[item.tipo_eneagrama] || 0) + 1
      }
    })
    return Array.from({ length: 9 }, (_, i) => ({
      tipo: i + 1,
      count: counts[i + 1] || 0,
    }))
  }, [filteredData])

  const totalEneagrama = eneagramaDistribution.reduce((sum, item) => sum + item.count, 0)

  const eneagramaChartData = eneagramaDistribution.map((item) => ({
    name: `Tipo ${item.tipo}`,
    fullName: ENEAGRAMA_NAMES[item.tipo],
    count: item.count,
    percentage: totalEneagrama > 0 ? ((item.count / totalEneagrama) * 100).toFixed(1) : "0",
  }))

  // HEBAG distribution
  const hebagDistribution = useMemo(() => {
    const counts: Record<string, number> = { H: 0, E: 0, B: 0, A: 0, G: 0 }
    filteredData.forEach((item) => {
      if (!item.teste_avancado_completo || !item.pontuacoes_avancadas) return
      const scores = Object.fromEntries(
        HEBAG_KEYS.map((k) => [k, item.pontuacoes_avancadas![k] ?? 0])
      )
      const primary = HEBAG_KEYS.reduce((a, b) => (scores[b] > scores[a] ? b : a))
      counts[primary]++
    })
    return HEBAG_KEYS.map((k) => ({ area: k, name: HEBAG_NAMES[k], count: counts[k] }))
  }, [filteredData])

  const totalHEBAG = hebagDistribution.reduce((sum, item) => sum + item.count, 0)

  const hebagChartData = hebagDistribution.map((item) => ({
    name: item.name,
    area: item.area,
    count: item.count,
    percentage: totalHEBAG > 0 ? ((item.count / totalHEBAG) * 100).toFixed(1) : "0",
  }))

  // Microperfil distribution
  const microperfilDistribution = useMemo(() => {
    const counts: Record<string, number> = {}
    filteredData.forEach((item) => {
      if (item.teste_avancado_completo && item.microperfil) {
        counts[item.microperfil] = (counts[item.microperfil] || 0) + 1
      }
    })
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [filteredData])

  // HEBAG averages
  const hebagAverages = useMemo(() => {
    const sums: Record<string, number> = { H: 0, E: 0, B: 0, A: 0, G: 0 }
    let count = 0
    filteredData.forEach((item) => {
      if (!item.teste_avancado_completo || !item.pontuacoes_avancadas) return
      HEBAG_KEYS.forEach((k) => { sums[k] += item.pontuacoes_avancadas![k] ?? 0 })
      count++
    })
    if (count === 0) return []
    return HEBAG_KEYS.map((k) => ({
      area: k,
      name: HEBAG_NAMES[k],
      average: Math.round((sums[k] / count) * 10) / 10,
    }))
  }, [filteredData])

  // Eneagrama x HEBAG correlation
  const correlation = useMemo(() => {
    const matrix: Record<number, Record<string, number>> = {}
    filteredData.forEach((item) => {
      if (!item.tipo_eneagrama || !item.teste_avancado_completo || !item.pontuacoes_avancadas) return
      if (!matrix[item.tipo_eneagrama]) {
        matrix[item.tipo_eneagrama] = { H: 0, E: 0, B: 0, A: 0, G: 0 }
      }
      const scores = Object.fromEntries(
        HEBAG_KEYS.map((k) => [k, item.pontuacoes_avancadas![k] ?? 0])
      )
      const primary = HEBAG_KEYS.reduce((a, b) => (scores[b] > scores[a] ? b : a))
      matrix[item.tipo_eneagrama][primary]++
    })
    return Object.entries(matrix)
      .map(([tipo, areas]) => ({ tipo: Number(tipo), ...areas }))
      .sort((a, b) => a.tipo - b.tipo)
  }, [filteredData])

  return (
    <AdminLayout title="Analytics - Dados Anônimos">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-muted-foreground">
            Estatísticas agregadas sem dados pessoais identificáveis
          </p>
        </div>
        <Button onClick={() => router.reload()} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards (global, sem filtro) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-full">
              <Activity className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Testes Básicos</p>
              <p className="text-2xl font-bold">{completion_stats.basic.complete}</p>
              <p className="text-xs text-muted-foreground">
                {completion_stats.basic.rate}% de {completion_stats.basic.total}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-500/10 rounded-full">
              <TrendingUp className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Testes Avançados</p>
              <p className="text-2xl font-bold">{completion_stats.advanced.complete}</p>
              <p className="text-xs text-muted-foreground">
                {completion_stats.advanced.rate}% de {completion_stats.advanced.total}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-full">
              <BarChart3 className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Eneagrama</p>
              <p className="text-2xl font-bold">{totalEneagrama}</p>
              <p className="text-xs text-muted-foreground">
                {hasFilters ? "nos filtros" : "perfis identificados"}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/10 rounded-full">
              <PieChart className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total HEBAG</p>
              <p className="text-2xl font-bold">{totalHEBAG}</p>
              <p className="text-xs text-muted-foreground">
                {hasFilters ? "nos filtros" : "microperfis identificados"}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Time Series (global) */}
      <Card className="p-6 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold">Testes Completados (Últimos 30 dias)</h2>
        </div>

        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={time_series}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={2} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="basic"
                name="Básico"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="advanced"
                name="Avançado"
                stroke="#f97316"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filtros</span>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFilterEscola(""); setFilterSerie(""); setFilterSala("") }}
              className="text-xs h-6 px-2"
            >
              Limpar
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Colégio</label>
            <select
              value={filterEscola}
              onChange={(e) => setFilterEscola(e.target.value)}
              className="border rounded-md px-3 py-2 w-full text-sm bg-white"
            >
              <option value="">Todos</option>
              {escolas.map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Série</label>
            <select
              value={filterSerie}
              onChange={(e) => setFilterSerie(e.target.value)}
              className="border rounded-md px-3 py-2 w-full text-sm bg-white"
            >
              <option value="">Todas</option>
              {series.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Sala</label>
            <select
              value={filterSala}
              onChange={(e) => setFilterSala(e.target.value)}
              className="border rounded-md px-3 py-2 w-full text-sm bg-white"
            >
              <option value="">Todas</option>
              {salas.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Eneagrama Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Distribuição Eneagrama</h2>
          </div>

          {totalEneagrama === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Sem dados de eneagrama para os filtros selecionados
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={eneagramaChartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const data = payload[0].payload
                      return (
                        <div className="bg-white border rounded-lg shadow-lg p-3">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm text-muted-foreground">{data.fullName}</p>
                          <p className="text-sm">
                            {data.count} pessoas ({data.percentage}%)
                          </p>
                        </div>
                      )
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {eneagramaChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={ENEAGRAMA_COLORS[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <PieChart className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Eneagrama (Pizza)</h2>
          </div>

          {totalEneagrama === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Sem dados de eneagrama para os filtros selecionados
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={eneagramaChartData.filter((d) => d.count > 0)}
                    dataKey="count"
                    nameKey="name"
                    outerRadius={100}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    labelLine={{ stroke: "#888", strokeWidth: 1 }}
                  >
                    {eneagramaChartData
                      .filter((d) => d.count > 0)
                      .map((item) => {
                        const tipo = Number(item.name.replace("Tipo ", ""))
                        return <Cell key={item.name} fill={ENEAGRAMA_COLORS[tipo - 1]} />
                      })}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* HEBAG Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Áreas Vocacionais (HEBAG)</h2>
          </div>

          {totalHEBAG === 0 ? (
            <div className="h-[250px] flex items-center justify-center text-muted-foreground">
              Sem dados de teste avançado para os filtros selecionados
            </div>
          ) : (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hebagChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const data = payload[0].payload
                      return (
                        <div className="bg-white border rounded-lg shadow-lg p-3">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm">
                            {data.count} pessoas ({data.percentage}%)
                          </p>
                        </div>
                      )
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {hebagChartData.map((item) => (
                      <Cell key={item.area} fill={HEBAG_COLORS[item.area]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <PieChart className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">HEBAG (Pizza)</h2>
          </div>

          {totalHEBAG === 0 ? (
            <div className="h-[250px] flex items-center justify-center text-muted-foreground">
              Sem dados de teste avançado para os filtros selecionados
            </div>
          ) : (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={hebagChartData.filter((d) => d.count > 0)}
                    dataKey="count"
                    nameKey="name"
                    outerRadius={80}
                    label={({ name, percentage }) => `${name} (${percentage}%)`}
                    labelLine={{ stroke: "#888", strokeWidth: 1 }}
                  >
                    {hebagChartData
                      .filter((d) => d.count > 0)
                      .map((item) => (
                        <Cell key={item.area} fill={HEBAG_COLORS[item.area]} />
                      ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Microperfil Distribution + HEBAG Averages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <PieChart className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Distribuição de Microperfis</h2>
          </div>

          {microperfilDistribution.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Sem dados de microperfil para os filtros selecionados
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={microperfilDistribution}
                    dataKey="count"
                    nameKey="name"
                    outerRadius={100}
                    label={({ name, count }) => `${name.replace(/^O /, "")} (${count})`}
                    labelLine={{ stroke: "#888", strokeWidth: 1 }}
                  >
                    {microperfilDistribution.map((_, index) => (
                      <Cell key={`mp-${index}`} fill={ENEAGRAMA_COLORS[index % ENEAGRAMA_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RePieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Média HEBAG — {filterLabel}</h2>
          </div>

          {hebagAverages.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              Sem dados de teste avançado para os filtros selecionados
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hebagAverages}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null
                      const data = payload[0].payload
                      return (
                        <div className="bg-white border rounded-lg shadow-lg p-3">
                          <p className="font-semibold">{data.name}</p>
                          <p className="text-sm">Média: {data.average}</p>
                        </div>
                      )
                    }}
                  />
                  <Bar dataKey="average" radius={[4, 4, 0, 0]}>
                    {hebagAverages.map((item) => (
                      <Cell key={item.area} fill={HEBAG_COLORS[item.area]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Eneagrama x HEBAG Correlation */}
      {correlation.length > 0 && (
        <Card className="p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold">Correlação Eneagrama × Área HEBAG</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Área primária dos alunos que completaram ambos os testes
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Tipo</th>
                  {HEBAG_KEYS.map((area) => (
                    <th key={area} className="text-center py-2 px-3" style={{ color: HEBAG_COLORS[area] }}>
                      {HEBAG_NAMES[area]}
                    </th>
                  ))}
                  <th className="text-right py-2 px-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {correlation.map((row) => {
                  const rowTotal = HEBAG_KEYS.reduce((sum, k) => sum + ((row as Record<string, number>)[k] || 0), 0)
                  const maxVal = Math.max(...HEBAG_KEYS.map((k) => (row as Record<string, number>)[k] || 0))
                  return (
                    <tr key={row.tipo} className="border-b hover:bg-muted/50">
                      <td className="py-2 px-3 font-medium">
                        Tipo {row.tipo} - {ENEAGRAMA_NAMES[row.tipo]}
                      </td>
                      {HEBAG_KEYS.map((area) => {
                        const val = (row as Record<string, number>)[area] || 0
                        const opacity = maxVal > 0 ? (val / maxVal) * 0.6 + (val > 0 ? 0.15 : 0) : 0
                        return (
                          <td
                            key={area}
                            className="text-center py-2 px-3 font-semibold"
                            style={{
                              backgroundColor: val > 0 ? `${HEBAG_COLORS[area]}${Math.round(opacity * 255).toString(16).padStart(2, "0")}` : "transparent",
                            }}
                          >
                            {val > 0 ? val : "-"}
                          </td>
                        )
                      })}
                      <td className="py-2 px-3 text-right font-semibold">{rowTotal}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Summary Table */}
      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Resumo por Tipo</h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">Tipo</th>
                <th className="text-left py-2 px-3">Nome</th>
                <th className="text-right py-2 px-3">Quantidade</th>
                <th className="text-right py-2 px-3">Porcentagem</th>
                <th className="text-left py-2 px-3">Visualização</th>
              </tr>
            </thead>
            <tbody>
              {eneagramaChartData.map((item, index) => (
                <tr key={item.name} className="border-b hover:bg-muted/50">
                  <td className="py-2 px-3 font-medium">{item.name}</td>
                  <td className="py-2 px-3 text-muted-foreground">{item.fullName}</td>
                  <td className="py-2 px-3 text-right font-semibold">{item.count}</td>
                  <td className="py-2 px-3 text-right">{item.percentage}%</td>
                  <td className="py-2 px-3">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${item.percentage}%`,
                          backgroundColor: ENEAGRAMA_COLORS[index],
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td colSpan={2} className="py-2 px-3">
                  Total
                </td>
                <td className="py-2 px-3 text-right">{totalEneagrama}</td>
                <td className="py-2 px-3 text-right">100%</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </AdminLayout>
  )
}
